import { useCallback, useRef, type ComponentPropsWithoutRef, type CSSProperties } from 'react';
import { Chart } from '@knack/asterisk-react';
import {
  Bar,
  BarChart,
  LabelList,
  Legend as RechartsLegend,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  type XAxisProps,
  type YAxisProps
} from 'recharts';
import { type LabelPosition } from 'recharts/types/component/Label';

import type { ChartData, ReportViewChart } from '@/types/schema/views/ReportView';
import { getDefaultChartLayout } from '@/utils/schema/patch/reports';
import { useChartContext } from './ChartContext';
import { ChartDataTable } from './shared/ChartDataTable';
import { ChartEmptyState } from './shared/ChartEmptyState';
import { ChartExportButton } from './shared/ChartExportButton';
import { ChartHeader } from './shared/ChartHeader';
import { useChart } from './shared/useChart';
import { useChartHelpers } from './shared/useChartHelpers';

const DEFAULT_BORDER_RADIUS = 5;
const DEFAULT_CHART_MARGIN = { left: 20, right: 20 };
const DEFAULT_FONT_SIZE = 12;
const DEFAULT_LABEL_LIST_OFFSET = 8;

interface BarChartHorizontalRenderProps extends ComponentPropsWithoutRef<'div'> {
  chart: ReportViewChart;
  chartData: ChartData;
  cssProps: CSSProperties;
}

export function BarChartHorizontalRender({
  chart,
  chartData,
  cssProps,
  ...rest
}: BarChartHorizontalRenderProps) {
  const { sourceTable } = useChartContext();
  const { getLegendAlignment, hasValidData, formatLabelValue } = useChartHelpers();
  const {
    chartRecords,
    chartKeys,
    chartCalculationByKeys,
    chartConfig,
    hasMultipleGroups,
    maxCalculationChartKey
  } = useChart({
    chart,
    chartData
  });

  const chartRef = useRef<HTMLDivElement>(null);

  const getChartHeight = useCallback(() => {
    // Date_time records can't be grouped. We need to display each day to prevent UI glitches. Therefore, we need to have a custom item height using a 25px offset
    if (chartRecords.length > 10) {
      return { height: chartRecords.length * 25 };
    }
    return {};
  }, [chartRecords.length]);

  if (!hasValidData(chartData, chartRecords)) {
    return <ChartEmptyState />;
  }

  const chartLayout = chart.layout || getDefaultChartLayout();

  const shouldUseVerticalLegend = chartLayout.legend === 'right' || chartLayout.legend === 'left';

  const shouldStackCalculations = chartLayout.stacking === 'normal';
  const shouldTiltLabels = chartLayout.tilt_labels;
  const { shouldShowDataTable, export_links: shouldShowExportButton } = chart.options;

  const chartLegendLayout = shouldUseVerticalLegend ? 'vertical' : 'horizontal';
  const chartLegendVerticalAlign = shouldUseVerticalLegend ? 'middle' : undefined;
  const chartLegendAlign = getLegendAlignment(chartLayout.legend);

  const yAxisConfig: YAxisProps = {
    type: 'category',
    tickLine: false,
    tickMargin: 10,
    axisLine: false,
    dataKey: 'group_0',
    tickFormatter: (value) =>
      formatLabelValue({
        value,
        chartCalculationByKeys,
        calculationKey: maxCalculationChartKey,
        sourceTable
      }),

    // Like in the v3, we need to set the interval to 0 to display every day in the chart vertically
    ...(chartRecords.length > 10 && { interval: 0 })
  };

  const xAxisConfig: XAxisProps = {
    type: 'number',
    hide: true,
    ...(shouldTiltLabels ? { angle: -45, textAnchor: 'end' } : {})
  };

  const addBarBorderRadius = (index: number) => {
    if (!shouldStackCalculations) {
      return DEFAULT_BORDER_RADIUS;
    }

    if (index === 0) {
      const borderRadius: [number, number, number, number] = [
        0,
        DEFAULT_BORDER_RADIUS,
        DEFAULT_BORDER_RADIUS,
        0
      ];
      return borderRadius;
    }

    if (index === Object.keys(chartKeys).length - 1) {
      const borderRadius: [number, number, number, number] = [
        DEFAULT_BORDER_RADIUS,
        0,
        0,
        DEFAULT_BORDER_RADIUS
      ] as const;
      return borderRadius;
    }

    return undefined;
  };

  return (
    <div className="flex-col">
      <div className="kn-bar-horizontal-chart-view" data-testid="bar-horizontal-chart" {...rest}>
        <ChartHeader chart={chart} />

        {shouldShowExportButton && <ChartExportButton chart={chart} chartRef={chartRef} />}

        <Chart
          ref={chartRef}
          config={chartConfig}
          className="w-full"
          style={{ ...cssProps, ...getChartHeight() }}
        >
          <BarChart
            accessibilityLayer
            style={cssProps}
            data={chartRecords}
            layout="vertical"
            margin={DEFAULT_CHART_MARGIN}
          >
            <YAxis {...yAxisConfig} />
            <XAxis {...xAxisConfig} />

            <RechartsTooltip cursor={false} content={<Chart.TooltipContent hideLabel />} />

            {chartLayout.legend !== 'none' && (
              <RechartsLegend
                content={<Chart.LegendContent />}
                layout={chartLegendLayout}
                verticalAlign={chartLegendVerticalAlign}
                align={chartLegendAlign}
              />
            )}
            {Object.keys(chartKeys).map((key, index) => {
              if (hasMultipleGroups && key === 'calc_0') {
                return null;
              }

              return (
                <Bar
                  key={`${chart.id}-bar-${key}`}
                  dataKey={key}
                  fill={`var(--color-${key})`}
                  radius={addBarBorderRadius(index)}
                  stackId={shouldStackCalculations ? '1' : undefined}
                >
                  {chartLayout.data_labels && (
                    <LabelList
                      dataKey={key}
                      position={(chartLayout.label_position as LabelPosition) ?? 'insideRight'}
                      fill={shouldStackCalculations ? '#FFF' : undefined}
                      offset={DEFAULT_LABEL_LIST_OFFSET}
                      formatter={(value: number) =>
                        formatLabelValue({
                          value,
                          calculationKey: maxCalculationChartKey,
                          chartCalculationByKeys,
                          sourceTable
                        })
                      }
                      className="fill-foreground"
                      fontSize={DEFAULT_FONT_SIZE}
                    />
                  )}
                </Bar>
              );
            })}
          </BarChart>
        </Chart>
      </div>
      {shouldShowDataTable && (
        <ChartDataTable chart={chart} chartRecords={chartRecords} chartConfig={chartConfig} />
      )}
    </div>
  );
}
